import { ColumnDetails } from '../../../grid/types/common';
import { html, TemplateResult } from 'lit';
import moment from 'moment/moment';
import { Column, ColumnKeys, GridEntry, GridEntryItem, PhasrMonitoredRulesGridEvents } from '../types';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents, SelectOption } from '../../../ui/gzSelect/types';
import { translateFn } from '../../../utils/translator.helper';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType, FontSize } from '../../../ui/gzIconButton/types';
import { PhasrRenderIconSet } from '../../../phasrRecommendations/uiUtils/iconSet';
import { PhasrIcons } from '../../../phasrRecommendations/uiUtils/types';
import { FontSizes } from '../../../types/common/types';
import { PhasrPanelComponentTypes } from '../../../phasrSidePanel/types/types';
import { getTimezone } from '../../../utils/global.utils';
import { BEHAVIORAL_PROFILE_DISPLAY_LIMIT } from '../../phasrMonitoredRulesDetailsPanel/types';
import { PhasrApplyActionSchema } from '../../../phasrApplyActionPanel/types';
import {
  getPhasrCategoryDictionaryToPrefferedName,
} from '../../../types/common/phasrTypes';

export function ruleNameRenderer(
  rowData: GridEntryItem,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {
  let value = String(rowData.item[columnDetails.key]);
  value = value.toString();

  if (!this.dictionary) {
    return html`<span class="cell-label">N/A</span>`;
  }

  const ruleName = this.dictionary[value]?.rule_name ?? 'N/A';

  return html`<span
    class="cell-label"
    style="color: var(--blue-layout-400-primary); cursor: pointer;"
    @click=${(event: Event) => {
      this.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.MonitoredRulesDetails, event.currentTarget, rowData.item);
    }}
    >${ruleName}</span
  >`;
}

export function recommendationsRenderer(
  rowData: GridEntryItem,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {
  const data = rowData.item[columnDetails.key];
  return html`<span class="cell-label" style="${data === 0 ? 'color: var(--body-text-arrows-400)' : 'color: var(--blue-layout-400-primary)'};
  ${ data === 0 ? 'cursor: default;' : 'cursor: pointer;' }"
                    @click=${(event: Event) => { if (data !== 0) { this.handleNavigateToRecommendationsClick(rowData.item)}}}
  >${data === 0 ? '-' : data}</span>`;
}

export function ruleTriggerDateRenderer(
  rowData: GridEntryItem,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {
  let data = '-';
  if (rowData.item[columnDetails.key]) {
    data = moment(rowData.item[columnDetails.key] * 1000).tz(getTimezone()).format('DD MMM YYYY, HH:mm:ss');
  }

  return html`<span class="cell-label">${data}</span>`;
}

export function targetedActivityTypeRenderer(
  rowData: GridEntryItem,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {

  let value = String(rowData.item[ColumnKeys.RULE_NAME]);
  value = value.toString();

  if (!this.dictionary) {
    return html`<span class="cell-label">N/A</span>`;
  }

  const category = getPhasrCategoryDictionaryToPrefferedName()[this.dictionary[value]?.['category']] ?? 'N/A';

  return html`<span class="cell-label">${category}</span>`;
}

export function ruleTriggersRenderer(
  rowData: GridEntryItem,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {
  const data = rowData.item[columnDetails.key];
  return html`<span class="cell-label" style="${data === 0 ? 'color: var(--body-text-arrows-400)' : 'color: var(--blue-layout-400-primary)'};
    ${ data === 0 ? 'cursor: default;' : 'cursor: pointer;' }"
    @click=${(event: Event) => { if (data !== 0) { this.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.RuleTriggers, event.currentTarget, rowData.item)}}}
  >${data === 0 ? '-' : data}</span>`;
}

export function behavioralProfilesRenderer(
  rowData: GridEntryItem,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {
  return html`
    <div
      class="d-flex align-items-center behavioral-profiles-icon"
      style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; ${
              rowData.item[columnDetails.key] === 0 ? 'cursor: default;' : 'cursor: pointer;'
      }" @click=${(event: Event) => {
        if (rowData.item[columnDetails.key] !== 0) {
            this.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.BehavioralProfiles, event.currentTarget, rowData.item)
        }
    }}
    >
      <gz-tooltip
        class="d-flex align-items-center"
        tooltipText="${rowData.item[columnDetails.key] > BEHAVIORAL_PROFILE_DISPLAY_LIMIT ? rowData.item[columnDetails.key] : ''}"
      >
        ${rowData.item[columnDetails.key] === 0
          ? html` <gz-text fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">-</gz-text>`
          : html`
              ${PhasrRenderIconSet.createIcon(
                PhasrIcons.BEHAVIORAL_PROFILES_ICON,
                ButtonColor.SECONDARY,
                ButtonSize.SIZE_24,
                'svg-icon'
              )}
              <gz-text style="padding-left: 3px" fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">
                ${rowData.item[columnDetails.key] > BEHAVIORAL_PROFILE_DISPLAY_LIMIT ? BEHAVIORAL_PROFILE_DISPLAY_LIMIT + '+' : rowData.item[columnDetails.key]}
              </gz-text>
            `}
      </gz-tooltip>
    </div>
  `;
}

export function restrictedBehavioralProfilesRenderer(
  rowData: GridEntryItem,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {
  return html`
    <div
      class="d-flex align-items-center behavioral-profiles-icon"
      style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; ${
              rowData.item[columnDetails.key] === 0 ? 'cursor: default;' : 'cursor: pointer;'
      }" @click=${(event: Event) => {
          if (rowData.item[columnDetails.key] !== 0) {
            this.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.RestrictedBehavioralProfiles, event.currentTarget, rowData.item)
          }
      }}
    >
      <gz-tooltip
        class="d-flex align-items-center"
        tooltipText="${rowData.item[columnDetails.key] > BEHAVIORAL_PROFILE_DISPLAY_LIMIT ? rowData.item[columnDetails.key] : ''}"
      >
        ${rowData.item[columnDetails.key] === 0
          ? html` <gz-text fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">-</gz-text>`
          : html`
              ${PhasrRenderIconSet.createIcon(
                PhasrIcons.BEHAVIORAL_PROFILES_ICON,
                ButtonColor.SECONDARY,
                ButtonSize.SIZE_24,
                'svg-icon'
              )}
              <gz-text style="padding-left: 3px" fontsize="${FontSizes.PARAGRAPH_12}" class="icon-value">
                ${rowData.item[columnDetails.key] > BEHAVIORAL_PROFILE_DISPLAY_LIMIT ? BEHAVIORAL_PROFILE_DISPLAY_LIMIT + '+' : rowData.item[columnDetails.key]}
              </gz-text>
            `}
      </gz-tooltip>
    </div>
  `;
}

export function companyRenderer(rowData: any, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  const companyName = rowData.item['companyName'];
  if (companyName) {
    return html`${companyName}`;
  }
  return translateFn('N/A');
}

export function selectColumnRenderer(rowData: GridEntryItem, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const options: Array<SelectOption> = [
    {
      title: translateFn('Edit access'),
      value: PhasrMonitoredRulesGridEvents.OnEditAccessFromGrid,
      id: rowData.item.objectId,
    }
  ];

  const gzSelect = new GzSelectWebComponent();
  const cellContainer = document.createElement('div');
  cellContainer.className = 'd-flex justify-end w-full';

  gzSelect.options = options;
  gzSelect.innerHTML = ` <gz-icon-button
          icon="ds-icon-more"
          fontSize="${FontSize.ICON_SIZE_24}"
          buttonType="${ButtonType.SQUARE}"
          buttonFill="${ButtonFill.QUIET}"
          buttonColor="${ButtonColor.SECONDARY}">
     </gz-icon-button>`;
  gzSelect.disabled = !rowData.item.phasrUsable || (rowData.item.behavioralProfiles === 0 && rowData.item.restrictedBehavioralProfiles === 0);

  const rule: GridEntry = rowData.item;
  gzSelect.addEventListener(SelectEvents.SELECTION_CHANGED, (e: CustomEvent) => {
    const selectedOption = e.detail.selected;

    const data: PhasrApplyActionSchema = {
      id: rule.objectId,
      companyId: rule.companyId,
      ruleId: rule.ruleId,
      description: this.dictionary[rule.ruleId]?.rule_description ?? 'N/A',
      targetedActivityType: getPhasrCategoryDictionaryToPrefferedName()[this.dictionary[rule.ruleId]?.['category']] ?? 'N/A',
      rule: rule.ruleTriggers,
    };

    const event = new CustomEvent(selectedOption, {
      detail: data,
      bubbles: true,
      composed: true,
    });

    this.dom.$grid.dispatchEvent(event);
  });
  cellContainer.appendChild(gzSelect);
  return cellContainer;
}
